import React, { useState } from 'react';
import { token } from '@atlaskit/tokens';
import { messages } from 'modules/mac/messages';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Checkbox } from '@atlaskit/checkbox';
import styled from 'styled-components';
import { PaymentMethodRow } from './PaymentMethodRow';
import Tooltip from 'components/common/Tooltip';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import t from 'modules/i18n/intl';
import { removeTimezone } from '../common/utils';

export const StyledWrapper = styled.div`
  display: flex;
  border-bottom: 1px solid #cccccc;
  padding: 10px;
  align-items: center;
`;
export const StyledValueWrapper = styled.div<{
  width?: string;
  textAlign?: string;
  isBold?: boolean;
}>`
  width: ${(props) => (props.width ? props.width : `unset`)};
  overflow-wrap: anywhere;
  margin-right: 10px;
  font-weight: ${(props) => (props.isBold ? `bold` : `unset`)};
`;

const displayCreditCardData = (cardData) => {
  let cardDetails: string | React.ReactNode = '';
  if (!cardData) {
    cardDetails = <FormattedMessage {...messages.noneProvided} />;
  } else if (cardData.gateway === 'BRAINTREE_PAYPAL') {
    cardDetails = `PAYPAL - ${cardData.name}`;
  } else {
    cardDetails = `${cardData.type} - ${cardData.maskedNumber} (${cardData.expiryMonth}/${cardData.expiryYear}) - ${cardData.name}`;
  }
  return cardDetails;
};

const displayAccountName = (account) => {
  let accountName: string | React.ReactNode = '';

  if (account?.accountFriendlyName) {
    accountName = account?.accountFriendlyName;
  } else if (account?.licenseId) {
    accountName = account?.organisation;
  } else {
    accountName = account?.name || account?.accountName;
  }
  return accountName || <FormattedMessage {...messages.notNamedYet} />;
};

export const PaymentMethodsTable = ({ paymentData, accountsSelected, setAccountsSelected }) => {
  const [isCheckAll, setIsCheckAll] = useState(false);
  const allIdsList = paymentData ? paymentData.map(({ id }) => id) : [];

  const onChangeCheckAll = () => {
    setAccountsSelected(isCheckAll ? [] : allIdsList);
    setIsCheckAll((current) => !current);
  };

  return (
    <>
      <StyledWrapper>
        <StyledValueWrapper width="5%">
          <Checkbox
            data-cy="select-all-checkbox"
            isChecked={isCheckAll}
            onChange={onChangeCheckAll}
          />
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="15%">
          <FormattedMessage {...messages.productTableTitle} />
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="15%">
          <FormattedMessage {...messages.nameTableTitle} />
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="15%">
          <FormattedMessage {...messages.SEN} />
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="14%">
          <FormattedMessage {...messages.renewsOnTableTitle} />
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="16%">
          <FormattedMessage {...messages.estimatedPriceTableTitle} />
          <Tooltip
            direction="right"
            content={<FormattedMessage {...messages.estimatedPriceTooltipText} />}
          >
            <QuestionCircleIcon
              label={t('mac.update.card.estimated.price.tooltip.text')?.toString() ?? ''}
              size="small"
              color={token('color.text.inverse', '#9FADBC')}
            />
          </Tooltip>
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="20%">
          <FormattedMessage {...messages.currentPaymentMethodTableTitle} />
        </StyledValueWrapper>
      </StyledWrapper>
      {paymentData?.map((account) => {
        return (
          <PaymentMethodRow
            key={`payment-method-row-${account?.id}`}
            isChecked={accountsSelected?.includes(account?.id)}
            setAccountsSelected={setAccountsSelected}
            product={
              account.merchandise.productPackage.key === 'atlassian.organisation'
                ? account.children[0].merchandise.details.description
                : account?.merchandise.details.description
            }
            name={displayAccountName(account)}
            sen={account?.id}
            renewsOn={
              <FormattedDate
                value={removeTimezone(account?.latestPeriod?.endDate ?? '')}
                year="numeric"
                month="short"
                day="2-digit"
              />
            }
            currentPaymentMethod={displayCreditCardData(account?.creditCardData)}
          />
        );
      })}
    </>
  );
};
